'use client'

import React, { Suspense, memo } from 'react'

import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  Award,
  Building2,
  Flame,
  type LucideIcon,
  MapPin,
  Shield,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react'

import { Skeleton } from '@/components/common/Skeleton'

import { cn } from '@/helpers/shadcn/utils'

import {
  useRecipientSectionQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { InsightSentiment, RecipientInsight } from '@/types/usaspending'

import { QueryErrorBoundary } from '../DataErrorBoundary'
import { useDetailFilter } from '../shared/DetailFilterContext'
import { InsightsUnavailable } from '../shared/ServiceUnavailable'
import { useRecipientDetail, useSectionHighlight } from './context'

// Highlight styling classes (matches SectionCard pattern)
const HIGHLIGHT_CLASSES =
  'ring-2 ring-accent-400 shadow-[0_0_0_4px_rgba(99,102,241,0.15)] dark:ring-accent-500'
const HIGHLIGHT_TRANSITION_IN = 'transition-all duration-200 ease-in'
const HIGHLIGHT_TRANSITION_OUT = 'transition-all duration-1000 ease-out'

/**
 * Map icon names from backend to Lucide components
 */
const ICON_MAP: Record<string, LucideIcon> = {
  trophy: Trophy,
  building: Building2,
  trending_up: TrendingUp,
  trending_down: TrendingDown,
  shield: Shield,
  flame: Flame,
  map: MapPin,
  award: Award,
  target: Target,
  users: Users,
  alert: AlertCircle,
}

/**
 * Sentiment-based color classes
 */
const SENTIMENT_COLORS: Record<InsightSentiment, string> = {
  positive: 'text-emerald-600 dark:text-emerald-400',
  negative: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  neutral: 'text-accent-600 dark:text-accent-400',
}

/**
 * RecipientDetail.Insights
 *
 * Displays pre-ranked insights from the backend.
 * The backend computes and ranks insights by notability, actionability, recency, and uniqueness.
 */
export function RecipientInsights() {
  const { recipientId } = useRecipientDetail()

  return (
    <QueryErrorBoundary
      title="Failed to load insights"
      serviceUnavailableFallback={<InsightsUnavailable />}
    >
      <Suspense fallback={<RecipientInsightsSkeleton />}>
        <RecipientInsightsContent id={recipientId} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function RecipientInsightsContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)
  const isHighlighted = useSectionHighlight('insights')

  if (dashboard.insights === null) {
    return <DeferredInsights id={id} isHighlighted={isHighlighted} />
  }

  const insights = dashboard.insights
  if (insights.length === 0) return null

  return (
    <div
      id="insights"
      className={cn(
        'flex flex-col rounded-xl border border-primary-200 bg-white shadow-sm hover:shadow-md dark:border-primary-700 dark:bg-primary-900',
        isHighlighted ? HIGHLIGHT_CLASSES : '',
        isHighlighted ? HIGHLIGHT_TRANSITION_IN : HIGHLIGHT_TRANSITION_OUT,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
          Quick Insights
        </h3>
      </div>
      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="space-y-2">
          {insights.map((insight) => (
            <InsightRow key={insight.id} insight={insight} />
          ))}
        </div>
      </div>
    </div>
  )
}

function DeferredInsights({
  id,
  isHighlighted,
}: {
  id: string
  isHighlighted: boolean
}) {
  const queries = useRecipientSectionQueries()
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data, isLoading } = useQuery(queries.insights(id, 4, fiscalYear))

  if (isLoading) return <RecipientInsightsSkeleton />
  if (!data || data.length === 0) return null

  return (
    <div
      id="insights"
      className={cn(
        'flex flex-col rounded-xl border border-primary-200 bg-white shadow-sm hover:shadow-md dark:border-primary-700 dark:bg-primary-900',
        isHighlighted ? HIGHLIGHT_CLASSES : '',
        isHighlighted ? HIGHLIGHT_TRANSITION_IN : HIGHLIGHT_TRANSITION_OUT,
      )}
    >
      <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
          Quick Insights
        </h3>
      </div>
      <div className="p-3 sm:p-4">
        <div className="space-y-2">
          {data.map((insight) => (
            <InsightRow key={insight.id} insight={insight} />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Memoized insight row to prevent unnecessary re-renders (rerender-memo)
 */
const InsightRow = memo(function InsightRow({
  insight,
}: {
  insight: RecipientInsight
}) {
  const Icon = ICON_MAP[insight.icon] ?? Award

  return (
    <div className="flex items-start gap-3 border-b border-primary-200/50 pb-2 last:border-0 last:pb-0 dark:border-primary-700/50">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <span
        className={cn(
          'min-w-[72px] font-mono text-sm font-bold',
          SENTIMENT_COLORS[insight.sentiment],
        )}
      >
        {insight.metric}
      </span>
      <span className="text-sm text-primary-700 dark:text-primary-300">
        {insight.text}
      </span>
    </div>
  )
})

function RecipientInsightsSkeleton() {
  return (
    <div className="flex flex-col rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
        <Skeleton className="h-3 w-24" />
      </div>
      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
